/* eslint-disable react/no-did-update-set-state */

import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import Dropzone from 'react-dropzone';

import StyledModal, { ModalActions } from '../Modal';
import Button from '../Button';
import * as Icons from '../icons/Icons';
import SinglePhotoManager from './SinglePhotoManager';
import { optimizeImage, generateImageName, getOrientation } from './utils/imageHelpers';

import { UPDATE } from './utils/UploadManager';
import { UPLOAD_MANAGER_SHAPE } from './shapes';

export default class PhotoGalleryManager extends React.PureComponent {
    static propTypes = {
        gallery: PropTypes.array,
        saveToGallery: PropTypes.func,
        onPublish: PropTypes.func,
        onDelete: PropTypes.func,
        onUpdate: PropTypes.func,
        uploadManager: UPLOAD_MANAGER_SHAPE.isRequired,
        onHeroPromotion: PropTypes.func
    };

    static defaultProps = {
        gallery: [],
        onPublish: () => { },
        onDelete: () => { },
        onUpdate: () => { },
        saveToGallery: () => { },
        onHeroPromotion: () => { }
    };

    state = {
        isHelpModalOpen: false,
        dropzoneActive: false,
        error: '',
        gallery: this.props.gallery,
        unpublishedItems: []
    };

    componentDidUpdate(prevProps) {
        const { gallery } = this.props;

        if (gallery !== prevProps.gallery) {
            this.setState({
                gallery
            });
        }
    }

    onDragEnter = () => {
        this.setState({
            dropzoneActive: true
        });
    };

    onDragLeave = () => {
        this.setState({
            dropzoneActive: false
        });
    };

    onDropRejected = () => {
        // Currently ignoring a bad file drop - no warning is presented, but
        // also the UI doesn't break.
    };

    onDrop = (acceptedFiles) => {
        this.setState({
            dropzoneActive: false
        });

        acceptedFiles.forEach((file) => {
            // Prevent memory leaks
            window.URL.revokeObjectURL(file.preview);

            const reader = new FileReader();

            reader.onload = (e) => {
                const result = e.target.result;
                const img = new Image();

                img.onload = async () => {
                    const exifOrientation = await getOrientation(result);

                    const droppedImage = {
                        source: result,
                        name: generateImageName(file.name),
                        size: file.size,
                        height: img.height,
                        width: img.width,
                        exifOrientation
                    };

                    // If the dropped image has a long edge longer than 4000px,
                    // we're going to downsize it to avoid crazy big images
                    // from getting uploaded
                    const originalImage = await optimizeImage(droppedImage);

                    const newItem = {
                        id:
                            Math.random()
                                .toString(36)
                                .substr(2, 9) + Date.now().toString(),
                        originalImage,
                        croppedImage: {
                            ...originalImage,
                            pixelCrop: {
                                x: 0,
                                y: 0,
                                width: originalImage.width,
                                height: originalImage.height
                            }
                        },
                        position: -1
                    };

                    this.setState({
                        unpublishedItems: [...this.state.unpublishedItems, newItem]
                    });
                };

                img.src = result;
            };

            if (file) {
                reader.readAsDataURL(file);
            }
        });
    };

    onPublish = (newItem) => {
        const unpublishedItems = this.state.unpublishedItems.filter((item) => item.id !== newItem.id);

        this.setState({
            gallery: [...this.state.gallery, newItem],
            unpublishedItems
        });
    };

    onCancel = (canceledItem) => {
        const unpublishedItems = this.state.unpublishedItems.filter((item) => item.id !== canceledItem.id);

        this.setState({ unpublishedItems });
    };

    onUpdate = (updatedItem) => {
        const gallery = this.state.gallery.map((item) => {
            if (item.id === updatedItem.id) {
                return updatedItem;
            }
            return item;
        });

        this.setState({ gallery });
        this.props.onUpdate();
    };

    getIsAltTextUnique = (input, id) => {
        const { gallery } = this.state;

        const galleryMinusThisOne = gallery.filter((item) => {
            return item.id !== id;
        });

        const index = galleryMinusThisOne.findIndex((item) => {
            return item.altText.trim().toUpperCase() === input.trim().toUpperCase();
        });

        if (index === -1) {
            return true;
        }

        return false;
    }

    deleteGalleryItem = (id) => {
        const gallery = this.state.gallery.filter((item) => item.id !== id);

        this.setState({ gallery });
    };

    reorderGallery = ({ oldIndex, newIndex }) => {
        const movedItem = this.state.gallery.find((item, index) => index === oldIndex);
        const otherItems = this.state.gallery.filter((item, index) => index !== oldIndex);
        const verifiedNewIndex = newIndex > this.state.gallery.length ? this.state.gallery.length - 1 : newIndex;

        const gallery = [...otherItems.slice(0, verifiedNewIndex), movedItem, ...otherItems.slice(verifiedNewIndex)];

        this.setState({ gallery });

        // get lowest of oldIndex and newIndex - from here down needs updating
        const lowestIndex = oldIndex < verifiedNewIndex ? oldIndex : verifiedNewIndex;

        // update every item that is at lowest index and higher
        gallery.forEach((item, index) => {
            if (index >= lowestIndex) {
                const image = {
                    credits: item.credit,
                    description: item.caption,
                    media_id: item.originalImage.mediaId,
                    title: item.altText,
                    url: item.croppedImage.source,
                    position: index,
                    is_gallery: true,
                    is_primary: false,
                    is_preview: false
                };
                image.position = index;

                const task = {
                    type: UPDATE,
                    image,
                    onCompletion: () => { },
                    onError: (err) => {
                        // eslint-disable-next-line no-console
                        console.warn('Error updating position:', err);
                    }
                };
                this.props.uploadManager.addTask(task);
            }
        });
    };

    toggleHelpModal = () => {
        this.setState({ isHelpModalOpen: !this.state.isHelpModalOpen });
    };

    renderGalleryItems = () => {
        const { gallery } = this.state;
        const { uploadManager, onHeroPromotion } = this.props;

        if (!gallery.length) {
            return null;
        }

        return gallery.map((galleryItem, index) => {
            return (
                <SinglePhotoManager
                    {...galleryItem}
                    key={galleryItem.id}
                    id={galleryItem.id}
                    imageType="gallery"
                    imageTypeDisplay="Gallery Image"
                    uploadManager={uploadManager}
                    onDelete={this.deleteGalleryItem}
                    onPublish={this.onPublish}
                    onUpdate={this.onUpdate}
                    position={index}
                    numberOfGalleryItems={gallery.length}
                    reorderGallery={this.reorderGallery}
                    onHeroPromotion={onHeroPromotion}
                    getIsAltTextUnique={this.getIsAltTextUnique} />
            );
        });
    };

    renderUnpublishedItems = () => {
        const { unpublishedItems, gallery } = this.state;
        const { uploadManager } = this.props;

        if (!unpublishedItems.length) {
            return null;
        }

        return unpublishedItems.map((galleryItem) => {
            return (
                <SinglePhotoManager
                    {...galleryItem}
                    key={galleryItem.id}
                    id={galleryItem.id}
                    imageType="gallery"
                    imageTypeDisplay="Gallery Image"
                    uploadManager={uploadManager}
                    onDelete={this.deleteGalleryItem}
                    onCancel={this.onCancel}
                    onPublish={this.onPublish}
                    numberOfGalleryItems={gallery.length}
                    getIsAltTextUnique={this.getIsAltTextUnique} />
            );
        });
    };

    render() {
        const { dropzoneActive, unpublishedItems, isHelpModalOpen, error } = this.state;
        const hasUnsavedGalleryImages = unpublishedItems.length > 0;
        const hasError = error && error.length > 0;
        let buttonText = '';

        if (hasUnsavedGalleryImages) {
            buttonText = 'Save Changes Before Adding Another Gallery Image';
        }
        else {
            buttonText = this.state.gallery.length ? 'Add Another Gallery Image' : 'Add Gallery Image';
        }

        return (
            <div className="rec-photo-gallery-manager">
                <h2 className="rec-photo-upload-page-section-heading">
                    <button
                        className="rec-photo-upload-page-section-helptip-trigger"
                        onClick={this.toggleHelpModal}>
                        Gallery Images <Icons.IconInfo alt="" />
                    </button>
                </h2>

                <div className="rec-photo-gallery-items-wrapper">
                    {this.renderGalleryItems()}
                    {this.renderUnpublishedItems()}

                    <Dropzone
                        multiple
                        className="rec-photo-gallery-dropzone"
                        accept="image/jpeg, image/png"
                        onDrop={this.onDrop}
                        onDragEnter={this.onDragEnter}
                        onDropRejected={this.onDropRejected}
                        onDragLeave={this.onDragLeave}
                        inputProps={{
                            id: `photo-files-input-gallery`,
                            className: 'rec-photo-manager-files-input'
                        }}>
                        <div className="rec-photo-gallery-upload-control">
                            <button className="rec-button-primary" disabled={hasUnsavedGalleryImages}>
                                {buttonText}
                            </button>
                            <div className={cx('rec-photo-gallery-dropzone-overlay', { active: dropzoneActive })}>
                                <div className="contents">
                                    <Icons.IconCloudUpload />
                                    <h3>Drop File to Upload Image to the Gallery</h3>
                                </div>
                            </div>
                            <div className={cx('rec-photo-gallery-dropzone-overlay', { active: dropzoneActive || hasError })}>
                                <div className="contents">
                                    <Icons.IconCloudUpload />
                                    <h3>Drop File to Upload Image to the Gallery</h3>
                                </div>
                            </div>
                        </div>
                    </Dropzone>
                </div>

                <StyledModal
                    heading="Gallery Images"
                    isOpen={isHelpModalOpen}
                    onRequestClose={this.toggleHelpModal}>
                    <div className="rec-photo-help-modal-content">
                        <p>
                            In addition to the hero and preview images, you&rsquo;re also able to upload other photos for use in an image gallery
                            section on the inventory page for your location. There is no limit on the number of images you can add to the gallery, so
                            feel free to include as many photos as you&rsquo;d like for your location.
                        </p>
                    </div>
                    <ModalActions>
                        <Button onClick={this.toggleHelpModal}>
                            Close
                        </Button>
                    </ModalActions>
                </StyledModal>
            </div>
        );
    }
}
